/** Base URL for API requests. In dev, Vite proxies /api to localhost:8000. */
export const API_BASE_URL = '/api';

/** Maximum file sizes for uploads */
export const FILE_LIMITS = {
  /** Maximum photo file size in bytes (10 MB) */
  MAX_PHOTO_SIZE: 10 * 1024 * 1024,
  /** Maximum audio file size in bytes (50 MB) */
  MAX_AUDIO_SIZE: 50 * 1024 * 1024,
  /** Maximum video file size in bytes (200 MB) */
  MAX_VIDEO_SIZE: 200 * 1024 * 1024,
  /** Maximum photos per inspection point */
  MAX_PHOTOS_PER_INSPECTION: 50,
  /** Maximum audio notes per inspection point */
  MAX_AUDIO_PER_INSPECTION: 10,
} as const;

/** Image compression settings */
export const IMAGE_SETTINGS = {
  /** Maximum dimension for compressed photos (width or height) */
  MAX_DIMENSION: 2048,
  /** JPEG compression quality (0-1) */
  COMPRESSION_QUALITY: 0.85,
  /** Thumbnail maximum dimension */
  THUMBNAIL_DIMENSION: 200,
  /** Thumbnail JPEG quality */
  THUMBNAIL_QUALITY: 0.6,
  /** Accepted MIME types for photo uploads */
  ACCEPTED_TYPES: ['image/jpeg', 'image/png', 'image/heic', 'image/webp'],
} as const;

/** Audio recording settings */
export const AUDIO_SETTINGS = {
  /** Preferred sample rate for audio recording */
  SAMPLE_RATE: 44100,
  /** Audio MIME types in preference order */
  PREFERRED_MIME_TYPES: [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/wav',
  ],
} as const;

/** Sync engine configuration */
export const SYNC_CONFIG = {
  /** Minimum interval between sync attempts in ms */
  MIN_SYNC_INTERVAL: 5_000,
  /** Maximum number of retry attempts for a failed sync item */
  MAX_RETRY_ATTEMPTS: 5,
  /** Base delay for exponential backoff in ms */
  BASE_RETRY_DELAY: 1_000,
  /** Maximum delay between retries in ms (5 minutes) */
  MAX_RETRY_DELAY: 300_000,
  /** Number of items to process per sync batch */
  BATCH_SIZE: 5,
} as const;

/** Application metadata */
export const APP_META = {
  NAME: 'StructureAI',
  COMPANY: 'StructureAI',
  TAGLINE: 'AI-Powered Structural Engineering',
  VERSION: '0.1.0',
} as const;

/** Structural element type labels */
export const ELEMENT_TYPE_LABELS: Record<string, string> = {
  foundation: 'Foundation',
  external_wall: 'External Wall',
  internal_wall: 'Internal Wall',
  floor: 'Floor',
  roof_structure: 'Roof Structure',
  roof_covering: 'Roof Covering',
  beam: 'Beam',
  column: 'Column',
  lintel: 'Lintel',
  chimney: 'Chimney',
  staircase: 'Staircase',
  retaining_wall: 'Retaining Wall',
  drainage: 'Drainage',
  bridge_deck: 'Bridge Deck',
  bridge_slab: 'Bridge Slab',
  bridge_beam: 'Bridge Beam',
  bridge_girder: 'Bridge Girder',
  bridge_parapet: 'Bridge Parapet',
  bridge_railing: 'Bridge Railing',
  wearing_surface: 'Wearing Surface',
  bridge_pier: 'Bridge Pier',
  bridge_abutment: 'Bridge Abutment',
  bridge_bearing: 'Bridge Bearing',
  pile_cap: 'Pile Cap',
  pile: 'Pile',
  approach_wall: 'Approach Wall',
  approach_segment: 'Approach Segment',
  wing_wall: 'Wing Wall',
  expansion_joint: 'Expansion Joint',
  other: 'Other',
};

/** Investigation sheet / test panel type labels */
export const TEST_PANEL_TYPE_LABELS: Record<string, string> = {
  half_cell: 'Half-Cell Panel',
  covermeter: 'Cover Meter Panel',
  resistivity: 'Resistivity Panel',
  breakout: 'Reinforcement Exposure Sheet',
  duct_tendon_exposure: 'Duct Tendon Exposure Sheet',
  pundit_array: 'Pundit Live Array Sheet',
};

/** Pundit Live Array NDT option sets */
export const PUNDIT_NDT_FACE_OPTIONS = ['north', 'east', 'south', 'west'] as const;

export const PUNDIT_NDT_ELEMENT_OPTIONS = [
  'abutment',
  'soffit',
  'pier',
  'deck',
  'elevation',
  'column',
  'cantilever',
  'beam',
  'girder',
  'slab',
] as const;

export const PUNDIT_TENDON_POSITION_OPTIONS = [
  'upper',
  'middle',
  'lower',
  'east_upper',
  'east_middle',
  'east_lower',
  'west_upper',
  'west_middle',
  'west_lower',
  'other',
] as const;

export const PUNDIT_ANOMALY_TYPE_OPTIONS = [
  'void',
  'air_gap',
  'grout_defect',
  'crack',
  'no_defect',
  'other',
] as const;

/** Asset kind constant mirrored from backend report_test_panel_assets.py */
export const PUNDIT_SCAN_ASSET_KIND = 'pundit_scan_image';

/** Defect type labels */
export const DEFECT_TYPE_LABELS: Record<string, string> = {
  cracking: 'Cracking',
  spalling: 'Spalling',
  corrosion: 'Corrosion',
  dampness: 'Dampness',
  deflection: 'Deflection',
  rot: 'Rot',
  insect_attack: 'Insect Attack',
  settlement: 'Settlement',
  none: 'None',
  other: 'Other',
};

/** RICS condition rating labels */
export const RICS_RATING_LABELS: Record<number, string> = {
  1: 'Rating 1 — No repair currently needed',
  2: 'Rating 2 — Defects requiring repair, not urgent',
  3: 'Rating 3 — Serious/urgent defects',
};

/** Risk priority labels */
export const PRIORITY_LABELS: Record<string, string> = {
  P1_immediate: 'P1 — Immediate Danger',
  P2_urgent: 'P2 — Urgent',
  P3_attention: 'P3 — Requires Attention',
  P4_monitoring: 'P4 — Monitoring',
  P5_satisfactory: 'P5 — Satisfactory',
};

/** Defect severity labels */
export const SEVERITY_LABELS: Record<string, string> = {
  minor: 'Minor',
  major: 'Major',
  critical: 'Critical',
};

/** BRE Digest 251 crack width category labels */
export const CRACK_WIDTH_LABELS: Record<string, string> = {
  '0_negligible': '0 — Negligible (< 0.1mm)',
  '1_very_slight': '1 — Very Slight (< 1mm)',
  '2_slight': '2 — Slight (< 5mm)',
  '3_moderate': '3 — Moderate (5-15mm)',
  '4_severe': '4 — Severe (15-25mm)',
  '5_very_severe': '5 — Very Severe (> 25mm)',
};

/** Crack pattern labels */
export const CRACK_PATTERN_LABELS: Record<string, string> = {
  vertical: 'Vertical',
  horizontal: 'Horizontal',
  diagonal: 'Diagonal',
  stepped: 'Stepped',
  map_pattern: 'Map / Crazing',
  radial: 'Radial',
};

/** Engineering condition rating labels (1-5) */
export const ENGINEERING_RATING_LABELS: Record<number, string> = {
  1: '1 — Good condition',
  2: '2 — Acceptable',
  3: '3 — Requires attention',
  4: '4 — Poor condition',
  5: '5 — Unacceptable / Dangerous',
};

/** Repair timeframe labels */
export const REPAIR_TIMEFRAME_LABELS: Record<string, string> = {
  immediate: 'Immediate',
  '1_3_months': '1-3 Months',
  '6_12_months': '6-12 Months',
  next_maintenance: 'Next Maintenance',
  monitoring_only: 'Monitoring Only',
};
